// @flow
/*global gproxy*/
import React from 'react';
import { observable, action, computed, runInAction, toJS } from 'mobx';
import User from '../models/User';
import i18n from 'i18next';
import uuid from 'uuid';
import { ghost } from '../utils/ghost';
import { detectAlpha2Country } from '../../../utils/country';
import Image from '../components/common/Image';
import { countryCodes, supportedIsoCountries } from '../../../stores/Constants';
import * as Sentry from '@sentry/react';

class UserStore {
  @observable isReady = false;
  @observable isLoading = false;
  @observable errors = undefined;
  @observable isLogged = false;
  @observable isAgent = false;

  @observable user: User;
  @observable addresses = [];
  @observable subscriptions = {};
  @observable preferences = {};

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  @action setError = (errorMsg) => {
    this.errors = errorMsg;
  };

  async init() {
    this.isReady = false;
    // GProxy

    const generatePassword = () => {
      const length = 16;
      let result = '';
      const characters =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const charactersLength = characters.length;
      for (let i = 0; i < length; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength),
        );
      }
      return result;
    };

    const register2 = (email, pass, token, result) => {
      const prefs = window.gproxy.consents.serialize({
        profile: false,
        input: {
          GDPRPromo: false,
          GDPRPromo3rd: false,
          GDPRProfiling: false,
        },
      });

      const subs = {
        newsletter: {
          email: {
            isSubscribed: true,
          },
        },
        marketing: {
          email: {
            isSubscribed: true,
          },
        },
      };

      window.gproxy.accounts.register({
        email: email,
        password: pass,
        regToken: token,
        finalizeRegistration: true,
        preferences: prefs,
        subscriptions: subs,
        profile: {
          firstName: result.data.firstName,
          lastName: result.data.lastName,
          locale: 'en-GB',
        },
        data: {
          acquisitionSource: 'E-COMMERCE',
          acquisitionOwner: 'FHUK',
        },
        callback: function () {
          window.gproxy.accounts.login({
            callback: this.onLogin,
            loginID: email,
            password: pass,
            sessionExpiration: 0,
          });
        },
      });
    };

    const register = (email, pass, result) => {
      window.gproxy.accounts.initRegistration({
        callback: function () {
          register2(email, pass, result.token, result);
        },
      });
    };

    return ghost('gproxy').then(() => {
      this.isReady = true;
    });
  }

  @action
  async login(username, password, keepMeLogged) {
    this.isAgent = false;
    this.isLoading = true;
    this._username = username;
    this._password = password;

    return new Promise((resolve, reject) => {
      gproxy.accounts.login({
        callback: (response) => this.onLogin(response, resolve, reject),
        loginID: username,
        password: password,
        wormhole: false,
        sessionExpiration: !!keepMeLogged ? -2 : 0,
      });
    });
  }

  onLogin = async (response, resolve, reject) => {
    if (response.errorCode === 0) {
      this.getAccountInfo();
      resolve();
    } else {
      Sentry.captureException(
        new Error(
          `Gigya Error: ${response?.errorMessage} | ${response?.errorDetails} | ${response?.errorCode}`,
          {
            cause: response?.errorCode,
          },
        ),
        {
          tags: {
            errorType: 'login',
          },
        },
      );
      this.setIsLoading(false);
      this.setError(i18n.t('errors:login'));
      reject();
    }
  };

  _resetCredentialsForm = () => {
    delete this._username;
    delete this._password;
  };

  changePreferences = async (preferences) => {
    const params = {
      preferences,
      callback: (res) => {
        this.setAccountInfoResponse(res);
      },
    };
    gproxy.accounts.setAccountInfo(params);
  };

  @action
  restoreSession = () => {
    if (gproxy.session.valid()) {
      this.setIsLoading(true);
      this.getAccountInfo();
    } else {
      this.setIsLogged(false);
    }
  };

  getAccountInfo = () => {
    gproxy.accounts.getAccountInfo({
      callback: this.prepareAccountInfo,
      extraProfileFields: 'phones',
      include: 'profile,data,preferences,subscriptions,',
    });
  };

  getResetApi = () => {
    const self = this;
    let api = process.env.REACT_APP_GIGYA_RESET_API;
    if (self.rootStore.uiStore.site) {
      const testKey =
        'REACT_APP_GIGYA_RESET_API_' +
        self.rootStore.uiStore.site.toUpperCase();
      if (process.env[testKey]) {
        api = process.env[testKey];
      }
    }

    return api;
  };

  @action
  sendResetPassword = (id, lang, cb) => {
    let self = this;
    self.isLoading = true;
    self.setError('');

    gproxy.accounts.isAvailableLoginID({
      loginID: id,
      callback: (res) => {
        if (!res || res.errorCode !== 0 || res.isAvailable) {
          self.isLoading = false;
          self.setError(i18n.t('errors:resetPassword'));
        } else {
          gproxy.accounts.resetPassword({
            loginID: id,
            APIKey: self.getResetApi(),
            lang: lang ? lang.split('-')[0].split('_')[0] : 'en',
            callback: function (response) {
              self.isLoading = false;
              if (response.errorCode === 0 && cb) {
                cb(response);
              } else {
                Sentry.captureException(
                  new Error(
                    `Gigya Error: ${response?.errorMessage} | ${response?.errorDetails} | ${response?.errorCode}`,
                    {
                      cause: response?.errorCode,
                    },
                  ),
                  {
                    tags: {
                      errorType: 'reset-password',
                    },
                  },
                );
                self.setError(i18n.t('errors:resetPassword'));
              }
            },
          });
        }
      },
    });
  };

  @action
  sendActivateAccount = (id, lang, cb) => {
    let self = this;
    self.isLoading = true;
    self.setError('');
    const key =
      detectAlpha2Country() === countryCodes.us
        ? process.env.REACT_APP_GIGYA_ACTIVATE_KEY_US
        : process.env.REACT_APP_GIGYA_ACTIVATE_KEY;
    gproxy.accounts.resetPassword({
      loginID: id,
      APIKey: key,
      lang: lang ? lang.split('-')[0].split('_')[0] : 'en',
      callback: function (response) {
        self.isLoading = false;
        if (response.errorCode === 0 && cb) {
          cb(response);
        } else {
          self.setError(i18n.t('errors:resetPassword'));
        }
      },
    });
  };

  @action
  agentSearch = (handle, secret) => {
    this.isLoading = true;
    this.isAgent = true;

    return new Promise((resolve, reject) => {
      gproxy.protected.authenticate({
        client: 'agent-console',
        secret: secret,
        handle: handle,
        sessionExpiration: 0,
        callback: (response) => this.onLogin(response, resolve, reject),
      });
    });
  };

  // consents.update
  @action
  getSubscriptionText = (cb) => {
    window.gproxy.consents.profile({
      profile: this.user.acquisitionOwner.endsWith('US') ? 'us' : undefined,
      lang:
        this.rootStore.uiStore.language === 'cz'
          ? 'cs'
          : this.rootStore.uiStore.language,

      require: window.gproxy.consents.grants.MISSING_ONLY,
      callback: function (response) {
        cb(response);
      },
    });
  };

  @action
  activateProfile = (newPassword, passwordResetToken, apikey, lang, cb) => {
    let self = this;
    self.isLoading = true;
    self.setError('');
    gproxy.accounts.resetPassword({
      passwordResetToken,
      newPassword,
      lang: lang ? lang.split('-')[0].split('_')[0] : 'en',
      APIKey: apikey,
      callback: function (response) {
        self.isLoading = false;
        if (response.errorCode === 0) {
          if (cb) {
            cb(response);
          }
        } else {
          self.isLoading = false;
          if (
            response.errorMessage.toLowerCase().replace(/\s/g, '') ===
            'invalidtoken'
          ) {
            self.setError('errors:invalidtoken');
          } else {
            self.setError(response.errorMessage + ': ' + response.errorDetails);
          }
        }
      },
    });
  };

  @action
  customerCheck = () => {
    gproxy.customer.check().catch((err) => {
      if (err.errorCode === 401) {
        this.logout();
      }
    });
  };

  @action
  prepareAccountInfo = (response) => {
    if (response.errorCode === 0) {
      this.user = {
        ...this.user,
        UID: response.UID,
        identityId: response.data.identityId,
        name: response.profile.firstName ? response.profile.firstName : '',
        lastName: response.profile.lastName ? response.profile.lastName : '',
        phone: response?.data?.billingAddress?.phoneLineNumber
          ? `${(
              response?.data?.billingAddress?.phoneCountryCode || ''
            ).trim()} ${(
              response?.data?.billingAddress?.phoneLineNumber || ''
            ).trim()}`
          : (response?.data?.billingAddress?.phoneNumber || '').trim(),
        email: response.profile.email ? response.profile.email : '',
        gender: response.profile.gender ? response.profile.gender : undefined,
        birthDay: response.profile.birthDay
          ? response.profile.birthDay
          : undefined,
        birthMonth: response.profile.birthMonth
          ? response.profile.birthMonth
          : undefined,
        birthYear: response.profile.birthYear
          ? response.profile.birthYear
          : undefined,
        avatar: response.profile.photoURL ? response.profile.photoURL : '',
        smallAvatar: response.profile.thumbnailURL
          ? response.profile.thumbnailURL
          : '',
        subscriptions: response.subscriptions ? response.subscriptions : '',
        preferences: response.preferences ? response.preferences : '',
        acquisitionOwner: response?.data?.acquisitionOwner,
        acquisitionOwnerCountry:
          response?.data?.acquisitionOwner?.substring(2, 4) ?? null,
        billingCountry: response?.data?.billingAddress?.normCountryCode,
        billingAddress: response?.data?.billingAddress,
      };

      this.prepareAddresses(response);
      this.setIsLogged(true);
      this.setIsLoading(false);
    } else {
      this.setIsLogged(false);
      this.setIsLoading(false);
    }
  };

  @action prepareAddresses = (response) => {
    const data = response?.data?.shippingAddress;
    if (!data) {
      this.addresses = [];
      return;
    }

    this.addresses = (Array.isArray(data) ? data : [data]).map((el, index) => {
      const entry = Array.isArray(el) ? el[0] : el;
      return {
        ...entry,
        id: index,
        rndId: entry.rndId || uuid.v4(),
      };
    });
  };

  updateAddress = (address, cb) => {
    this.rootStore.uiStore.setLoader(true);
    const shippingAddress = address.map((el) => {
      const newEl = {
        ...el,
      };

      delete newEl.id;
      delete newEl.rndId;
      delete newEl.isNew;

      newEl.pudo = newEl.pudo?.lockerId ? newEl.pudo : null;

      return newEl;
    });

    var params = {
      data: {
        shippingAddress: shippingAddress,
      },
      callback: (res) => {
        this.setAccountInfoResponse(res, cb);
      },
    };

    gproxy.accounts.setAccountInfo(params);
  };

  @action
  logout = async () => {
    this.isLoading = true;
    this.isAgent = false;
    gproxy.accounts.logout({
      callback: this.onLogout,
    });
  };

  @action
  onLogout = () => {
    this.user = {
      ...this.user,
      name: '',
      lastName: '',
    };

    this.rootStore.orderStore.setCurrentOrder(undefined);
    this.rootStore.orderStore.setOrders([]);
    this.rootStore.orderStore.setShipments([]);
    this.isLoading = false;
    this.setIsLogged(false);
    window.location = '/' + detectAlpha2Country() + '/myarea';
  };

  updateUserData = (userInfo, cb) => {
    const country = detectAlpha2Country(); // Salviamo il valore per evitare chiamate multiple

    let params = {
      profile: {
        firstName: userInfo.name,
        lastName: userInfo.lastName,
        email: userInfo.email,
        gender: userInfo.gender,
        birthDay: userInfo.birthDay,
        birthMonth: userInfo.birthMonth,
        birthYear: userInfo.birthYear,
      },
      data: {
        billingAddress: {
          phoneNumber: userInfo?.phone,
          phoneCountryCode:
            country === countryCodes.us
              ? userInfo?.phone.substring(0, 2) || ''
              : country === countryCodes.cz || country === countryCodes.sk
                ? userInfo?.phone.substring(0, 4) || ''
                : userInfo?.phone.substring(0, 3) || '',
          phoneLineNumber:
            country === countryCodes.us
              ? userInfo?.phone.substring(2) || ''
              : country === countryCodes.cz || country === countryCodes.sk
                ? userInfo?.phone.substring(4) || ''
                : userInfo?.phone.substring(3) || '',
        },
      },
      callback: (res) => {
        const isSuccess = res.errorCode === 0;
        this.setAccountInfoResponse(res, () => {
          if (cb) cb(isSuccess);
        });
      },
    };

    if (userInfo.newPassword && userInfo.password) {
      params.newPassword = userInfo.newPassword;
      params.password = userInfo.password;
    }

    this.rootStore.uiStore.setLoader(true);
    gproxy.accounts.setAccountInfo(params);
  };

  updateUserPreferences = (preference) => {
    let params = {
      preference,
    };

    this.rootStore.uiStore.setLoader(true);
    gproxy.accounts.setAccountInfo(params);
  };

  // cb receives whether the write succeeded, so callers do not report a
  // success that never happened
  setAccountInfoResponse = (response, cb) => {
    if (response.errorCode === 400006) {
      if (cb) cb(false);
      this.rootStore.uiStore.setLoader(false);
      this.rootStore.uiStore.openModal(
        response.map((el) => el.message + ' - '),
        true,
      );
    } else if (response.errorCode === 403042) {
      if (cb) cb(false);
      this.rootStore.uiStore.setLoader(false);
      document.getElementById(
        'id-currentPassword',
      ).parentNode.style.borderColor = 'red';

      let p = document.createElement('p');
      p.setAttribute('id', 'error-current-psw');
      p.classList.add('invalidCurrentPsw');
      p.style.cssText =
        'margin: 0; font-size: 12px; margin-top: 3px; font-weight: normal; line-height: 1.17; letter-spacing: normal; color: #E8184C;';
      document.getElementById('id-currentPassword').parentNode.after(p);
      document
        .getElementById('error-current-psw')
        .append(i18n.t('errors:currentPasswordInvalid'));
      document.getElementById('id-currentPassword').value = '';
    } else if (response.errorCode === 401030) {
      if (cb) cb(false);
      this.rootStore.uiStore.setLoader(false);
      this.rootStore.uiStore.openModal(i18n.t('errors:oldPswUsed'), '', {
        icon: <Image name="warning"></Image>,
        isError: true,
      });
    } else if (response.errorCode === 0) {
      this.rootStore.uiStore.setLoader(false);
      this.rootStore.uiStore.openModal(i18n.t('changesSaved'), false);
      if (cb) cb(true);
      this.getAccountInfo();
    } else {
      if (cb) cb(false);
      this.rootStore.uiStore.setLoader(false);
      this.rootStore.uiStore.openModal(i18n.t('editProfileError'), '', {
        icon: <Image name="warning"></Image>,
        isError: true,
      });
    }
  };

  @action setIsLoading = (isLoading) => {
    this.isLoading = isLoading;
  };

  @action setIsLogged = (isLogged) => {
    this.isLogged = isLogged;
  };

  uploadProfilePhoto = async (photo) => {
    if (/\.(jpe?g|png|gif)$/i.test(photo.name)) {
      const result = await this.toBase64(photo).catch((e) => Error(e));
      if (result instanceof Error) {
      } else {
        const base64Photo = result.split(',')[1];
        const params = {
          photoBytes: base64Photo,
          publish: 'true',
          callback: this.profilePhotoResponse,
        };
        gproxy.accounts.setProfilePhoto(params);
      }
    } else {
      alert('Picture not in jpeg, png, gif format');
    }
  };

  removeProfilePhoto = async () => {
    const params = {
      profile: {
        photoURL: '',
      },
      callback: (res) => {
        this.setAccountInfoResponse(res);
      },
    };
    gproxy.accounts.setAccountInfo(params);
  };

  profilePhotoResponse = (response) => {
    if (response.errorCode === 0) {
      this.rootStore.uiStore.openModal(i18n.t('changesSaved'), false);
      this.getAccountInfo();
    } else {
      this.rootStore.uiStore.openModal(response.errorMessage, '', {
        isError: true,
      });
    }
  };

  resetPassword = (email) => {
    const params = {
      loginID: email,
    };
    return new Promise((resolve, reject) => {
      gproxy.accounts.resetPassword({
        ...params,
        callback: (response) => {
          if (response.errorCode === 0) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      });
    });
  };

  changePassword = (passwordResetToken, email, newPassword) => {
    const params = {
      loginID: email,
      passwordResetToken,
      newPassword,
    };
    return new Promise((resolve, reject) => {
      gproxy.accounts.resetPassword({
        ...params,
        callback: (response) => {
          if (response.errorCode === 0) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      });
    });
  };

  @action
  fetchPreferences = (email, uid) => {
    gproxy.subscriptions.list({
      email: email,
      uid: uid,
      callback: (response) => {
        if (response.errorCode === 0) {
          runInAction(() => {
            this.preferences = response.preferences;
          });
        } else {
          this.rootStore.uiStore.openModal(response.errorDetails, '', {
            isError: true,
          });
        }
      },
    });
  };

  @action
  fetchSubscriptions = (email, uid) => {
    gproxy.subscriptions.list({
      email: email,
      uid: uid,
      callback: (response) => {
        if (response.errorCode === 0) {
          runInAction(() => {
            this.subscriptions = response.subscriptions;
          });
        } else {
          this.rootStore.uiStore.openModal(response.errorDetails, '', {
            isError: true,
          });
        }
      },
    });
  };

  @action
  getSubscriptions = (email, uid) => {
    gproxy.subscriptions.list({
      email: email,
      uid: uid,
      callback: (response) => {
        if (response.errorCode === 0) {
          runInAction(() => {
            return response.subscriptions;
          });
        } else {
          this.rootStore.uiStore.openModal(response.errorDetails, '', {
            isError: true,
          });
        }
      },
    });
  };
  @action
  subscribe = (req) => {
    return new Promise((resolve, reject) => {
      gproxy.subscriptions.upsert({
        ...req,
        callback: (response) => {
          if (response.errorCode === 0) {
            runInAction(() => {
              this.subscriptions = req.subscriptions;
            });
            resolve(response);
          } else {
            reject(response);
          }
        },
      });
    });
  };

  @computed
  get hasSubscriptions() {
    const subs = toJS(this.subscriptions);
    if (Object.keys(subs).length === 0) {
      return false;
    }

    for (let key in subs) {
      if (subs[key] === true) {
        return true;
      }
    }
    return false;
  }

  @computed
  get hasPreferences() {
    let prefs = this.preferences;
    if (prefs) {
      const newPrefs = toJS(prefs);
      return (
        Object.keys(newPrefs).length > 0 &&
        Object.keys(newPrefs).find((el) => !!newPrefs[el])
      );
    }
    return false;
  }

  hasAgentCookie = () => {
    return gproxy.session && gproxy.session.trusted();
  };

  hasLoginCookie = () => {
    return !!(
      gproxy.session &&
      (gproxy.session.valid() || gproxy.session.native())
    );
  };

  toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  getCountryFromAcquisitionOwner = (acquisitionOwner) => {
    return acquisitionOwner === countryCodes.uk.toUpperCase()
      ? supportedIsoCountries.gb
      : acquisitionOwner;
  };
  getCountryFromIdentityId = (identityId) => {
    const country = identityId.slice(0, 2);
    return country === countryCodes.uk.toUpperCase()
      ? supportedIsoCountries.gb
      : country;
  };
}

export default UserStore;
